import React, { useEffect, useState } from 'react';
import {
  Text,
  Container,
  Flex,
  StyleSheet,
  CheckBox,
  Button,
  Loader,
  Icon,
  Chip,
} from '@shopuptech/expo-shopup-ui';
import {
  entityDetailsParamsAtom,
  getEntityActionsSelector,
  getEntityDetailsSelector,
} from '../../NoPermissionScreen/store/atoms';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { getApprovalActionResponse, getLoadableData } from '../Utils/helpers';
import {
  allowedEntityActionFallback,
  performApprovalEntityAction,
} from '../../NoPermissionScreen/store/apiHelpers';
import { AllowedActions } from '../../../modules/NoPermissionScreen/store/types';
import { useParams } from 'react-router-dom';
import { commonStyles } from '../../../styles';
import { useUserLogin } from '../../../hooks/useUserLogin';
import { GRAY_90 } from '@shopuptech/expo-shopup-ui/UikitUtils/colors';

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    gap: 16,
    flex: 1,
  },

  details: {
    display: 'flex',
    gap: 12,
  },
  lineItem: {
    marginVertical: 4,
  },
  label: {
    marginRight: 16,
    width: 200,
  },
  buttonContainer: {
    display: 'flex',
    gap: 12,
  },
  button: {
    width: 150,
  },
  overAll: {
    margin: 24,
  },
});

const Label = ({ label, middle }: { label: string; middle?: boolean }) => (
  <Flex overrideStyle={styles.label} middle={middle}>
    <Text lineHeight={22} size={14} color="gray50">
      {label}
    </Text>
  </Flex>
);

const Value = ({
  value,
  bold,
  testID,
}: {
  value: string;
  bold?: boolean;
  testID?: string;
}) => (
  <Text
    flex={1}
    lineHeight={22}
    size={14}
    color="gray30"
    overrideStyle={styles.label}
    bold={bold}
    testID={testID}>
    {value}
  </Text>
);

const LineItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <Flex flex={1} row wrap overrideStyle={styles.lineItem}>
      <Label label={label} middle />
      <Value value={value} />
    </Flex>
  );
};

const GrantAccess = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const setEntityId = useSetRecoilState(entityDetailsParamsAtom);

  useEffect(() => {
    setEntityId({ entityId });
  }, []);

  const [actionResponse, setActionResponse] = useState('');
  const [grantAccess, setGrantAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isRequestAccessFlow } = useUserLogin();
  const detailsLoadable = useRecoilValueLoadable(getEntityDetailsSelector);
  const { isLoading, response: data } = getLoadableData(
    detailsLoadable,
    undefined,
  );

  const allowedActionsLoadable = useRecoilValueLoadable(
    getEntityActionsSelector,
  );
  const { isLoading: allowedActionsLoading, response: allowedActions } =
    getLoadableData(allowedActionsLoadable, allowedEntityActionFallback);

  const loader = isLoading || allowedActionsLoading || loading;
  const isAccept =
    allowedActions?.allowedActions?.includes('Approve') && grantAccess;
  const isReject = allowedActions?.allowedActions?.includes('Reject');

  const handleGrantAccess = () => {
    setGrantAccess((prev) => !prev);
  };

  const handleAction = async (action: AllowedActions) => {
    setLoading(true);
    const payload = {
      entity_id: Number(entityId),
      user_action: action,
    };
    const response = await performApprovalEntityAction(payload);
    setActionResponse(getApprovalActionResponse(action, response));
    setLoading(false);
  };

  const handleApprove = () => handleAction('Approve');
  const handleReject = () => handleAction('Reject');

  if (!isRequestAccessFlow) return null;
  if (!data) return null;
  if (actionResponse) {
    return (
      <Flex center middle overrideStyle={commonStyles.overAllConatiner}>
        <Flex center overrideStyle={commonStyles.overAll}>
          <Icon name="Information" size="extralarge" />

          <Text center bold size={16} overrideStyle={commonStyles.accessText}>
            {actionResponse}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex overrideStyle={styles.overAll}>
      <Flex overrideStyle={styles.container}>
        {loader && <Loader />}
        <Flex>
          <Text bold size={24}>
            Access Approval Request
          </Text>
          <Text color="gray50">Review the following access request.</Text>
        </Flex>

        <Container
          corner="curved"
          shadow="shadow1"
          verticalPadding="24"
          horizontalPadding="16">
          <Flex overrideStyle={styles.details}>
            <Flex>
              <Text size={18} bold>
                Details
              </Text>
              <hr
                style={{
                  borderTop: `1px ${GRAY_90}`,
                  margin: '16px 0 0 0',
                }}
              />
            </Flex>
            <LineItem label="Name" value={data.data.user_name} />
            <LineItem label="Employee Id" value={data.data.user_id} />
            <LineItem label="Email Id" value={data.data.user_email} />

            <Flex row>
              <Label label="Requested Roles" />
              <Flex flex={1} row wrap overrideStyle={styles.details}>
                {data.data.role_names?.split(',').map((role) => (
                  <Flex key={role}>
                    <Chip key={role} testID={role} message={role} type="info" />
                  </Flex>
                ))}
              </Flex>
            </Flex>

            <Flex row>
              <Label label="Requested Scopes" />
              <Flex flex={1} row wrap overrideStyle={styles.details}>
                {data.data.scope_ids?.split(',').map((role) => (
                  <Text key={role} size={14} color="gray30" bold>
                    {`·  ${role}`}
                  </Text>
                ))}
              </Flex>
            </Flex>
            {data.data.comments && (
              <LineItem label="Comments" value={data.data.comments} />
            )}
          </Flex>
        </Container>

        <Flex row center between wrap>
          <Flex row center>
            <CheckBox
              testID="grant-checkbox"
              checked={grantAccess}
              onClick={handleGrantAccess}
            />
            <Text>I agree to grant access to the roles.</Text>
          </Flex>

          <Flex row overrideStyle={styles.buttonContainer}>
            <Flex overrideStyle={styles.button}>
              <Button
                testID="reject-button"
                contain
                buttonType="secondary"
                buttonSize="medium"
                disabled={!isReject}
                onClick={handleReject}>
                Reject
              </Button>
            </Flex>
            <Flex overrideStyle={styles.button}>
              <Button
                testID="accept-button"
                contain
                buttonSize="medium"
                disabled={!isAccept}
                overrideStyle={styles.button}
                onClick={handleApprove}>
                Grant Access
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default GrantAccess;
